import {
    Flowchart as FlowchartModel,
    FlowchartEdge,
    FlowchartNode,
    EdgeOptions
} from "@/core/interfaces/flowchart/flowchart";
import {Edge, Node} from "@vue-flow/core";
import {ProductRecommendation} from "@/core/interfaces/Product";

/**
 * Flowchart class to manage flowchart data and highlight paths
 */
export class Flowchart {
    private readonly data: FlowchartModel;
    private paths: string[][] = [];
    private selectedPathIndex: number = 0;
    private secondaryHighlight: boolean = true;

    constructor(data: FlowchartModel) {
        this.data = data;
    };

    /**
     * highlight paths to a given edge.
     */
    highlightToEdge(edge: Edge): void {
        this.findPathsToNode(edge.target);
        this.selectedPathIndex = this.pathIndexOfEdge(edge.id);
        this.secondaryHighlight = false;
    }

    /**
     * highlight paths to a given node.
     */
    highlightToNode(node: Node): void {
        this.findPathsToNode(node.id.toString());
        this.secondaryHighlight = true;
        const edges: FlowchartEdge[] = this.incomingEdges(parseInt(node.id));

        if (edges.length > 0) {
            this.selectedPathIndex = this.pathIndexOfEdge(Flowchart.edgeId(edges[0]));
        } else {
            this.selectedPathIndex = 0;
        }
    }

    /**
     * Find all paths to a specific node in the flowchart
     */
    findPathsToNode(targetNodeId: string): void {
        this.paths = [];

        if (this.data.nodes && this.data.nodes.length > 0) {
            const rootNode = this.data.nodes[0];
            this.findPaths(targetNodeId, rootNode.id.toString(), [], new Set());
        }
    }

    /**
     * Recursive function to find all paths to a target node
     */
    findPaths(targetNodeId: string, nodeId: string, currentPath: string[], visited: Set<string>) {
        if (visited.has(nodeId)) return;

        const newVisited = new Set(visited).add(nodeId);

        if (nodeId === targetNodeId) {
            this.paths.push(currentPath);
            return;
        }

        const outgoingEdges = this.data.edges.filter(edge =>
            edge.from_node_id.toString() === nodeId
        );

        for (const edge of outgoingEdges) {
            const newPath = [...currentPath, Flowchart.edgeId(edge)];
            this.findPaths(targetNodeId, edge.to_node_id.toString(), newPath, newVisited);
        }
    }

    /**
     * Check if a node is in the current path
     */
    isNodeHighlighted(nodeId: number): boolean {
        return !!this.paths.length
            && !!this.incomingEdges(nodeId)
                .filter(edge => this.paths[this.selectedPathIndex].includes(Flowchart.edgeId(edge)))
                .length;
    }

    /**
     * Check if an edge is highlighted in the current path
     */
    isEdgeHighlighted(edge: FlowchartEdge): boolean {
        return !!this.paths.length
            && this.paths[this.selectedPathIndex].includes(Flowchart.edgeId(edge));
    }

    /**
     * Check if an edge is secondary highlighted in the current path
     */
    isEdgeSecondaryHighlighted(edge: FlowchartEdge): boolean {
        return this.isEdgeHighlighted(edge) ||
            (this.secondaryHighlight && this.pathIndexOfEdge(Flowchart.edgeId(edge)) !== -1);
    }


    /**
     * Get the path index of a specific edge
     */
    pathIndexOfEdge(edgeId: string): number {
        return this.paths.findIndex(path => path.includes(edgeId));
    }

    /**
     * Generate a unique identifier for an edge based on its nodes
     */
    static edgeId(edge: FlowchartEdge): string {
        return 'e' + edge.from_node_id + '->' + edge.to_node_id;
    }

    /**
     *  Get all incoming edges for a specific node
     */
    incomingEdges(nodeId: number): FlowchartEdge[] {
        return this.data.edges.filter(edge => edge.to_node_id === nodeId);
    }

    /**
     * The inherent flowchart data.
     */
    model(): FlowchartModel {
        return this.data;
    }

    /**
     * Get the source node of a given edge.
     * @param edge The edge to get the source node for.
     */
    sourceNode(edge: FlowchartEdge): FlowchartNode {
        return this.data.nodes.find(node => node.id === edge.from_node_id)!;
    }

    /**
     * Get the options value of the currently selected edges.
     */
    selectedEdges(): { id: number, nodeLabel: string; label: string; slug: string, options?: EdgeOptions }[] {
        return this.data.edges
            .filter(edge => this.isEdgeHighlighted(edge))
            .map(edge => ({
                id: edge.id,
                nodeLabel: this.sourceNode(edge).label,
                label: edge.label,
                slug: edge.slug,
                options: edge.options
            }));
    }

    /**
     * Get the last selected edge.
     */
    lastSelectedEdge(): FlowchartEdge | null {
        const selected = this.data.edges
            .filter(edge => this.isEdgeHighlighted(edge));

        return selected[selected.length - 1] ?? null;
    }

    /**
     * Get the recommendations depending on the last selected edge, if there are edge specific recommendations.
     */
    recommendations(): ProductRecommendation[] {
        return this.model()
            .recommendations
            .filter(rec => !rec.options || rec.options?.edge === this.lastSelectedEdge()?.slug);
    }
}
